// Entitlements E4 — admin types + helpers for the plan × feature matrix, per-workspace
// overrides and credits (symone-v2 gateway/api/admin_entitlements.py, migration 035).
// The server validates every value against its registry kind and re-checks the tier
// (matrix edits super_admin, overrides + credits platform_admin); these helpers only
// shape the UI and catch obvious typos before a request goes out.

export type FeatureKind = 'bool' | 'int' | 'enum'
export type FeatureValue = boolean | number | string | null

export interface RegistryFeature {
  key: string
  kind: FeatureKind
  nullable: boolean
  values: string[] | null
  default: FeatureValue
  label: string
  group: string
  remedy: string
}

export interface MatrixCell {
  value: FeatureValue
  updated_at: string
  updated_by: string | null
}

export interface MatrixPlan {
  name: string
  display_name: string | null
  family: string
  billing_kind: 'monthly' | 'lifetime' | 'custom'
  sort_order: number
  is_active: boolean
  features: Record<string, MatrixCell>
}

export interface MatrixResponse {
  family: string
  families: string[]
  plans: MatrixPlan[]
  features: RegistryFeature[]
}

export interface PlanFeaturesResult {
  plan: string
  changed: boolean
  changes: Record<string, { from: FeatureValue; to: FeatureValue }>
}

export interface WorkspaceOverride {
  key: string
  value: FeatureValue
  note: string | null
  set_by: string
  expires_at: string | null
  created_at: string
  expired: boolean
  protected: boolean
  registered: boolean
  label: string | null
  kind: FeatureKind | null
}

export interface OverridesResponse {
  workspace_id: string
  overrides: WorkspaceOverride[]
  total: number
  protected: number
}

export interface LedgerEntry {
  id: string
  delta: number
  balance_after: number
  reason: 'grant' | 'top_up' | 'admin_adjust'
  actor_user_id: string | null
  note: string | null
  created_at: string
}

export interface CreditsResponse {
  workspace_id: string
  balance: number
  ledger: LedgerEntry[]
  has_more: boolean
}

export interface TopUpResult {
  workspace_id: string
  balance: number
  entry: LedgerEntry
}

export const MATRIX_QUERY_KEY = 'admin-plan-matrix'
export const REGISTRY_QUERY_KEY = ['admin-entitlements-registry'] as const
export const OWNER_PROTECTION_PREFIX = 'owner protection'
export const MAX_TOP_UP = 100_000_000
const MAX_INT = 2_147_483_647

export const matrixPath = (family: string) =>
  `/admin/plans/matrix?family=${encodeURIComponent(family)}`
export const planFeaturesPath = (plan: string) =>
  `/admin/plans/${encodeURIComponent(plan)}/features`
export const overridesPath = (workspaceId: string) =>
  `/admin/workspaces/${encodeURIComponent(workspaceId)}/feature-overrides`
export const overridePath = (workspaceId: string, key: string) =>
  `${overridesPath(workspaceId)}/${encodeURIComponent(key)}`
export const creditsPath = (workspaceId: string) =>
  `/admin/workspaces/${encodeURIComponent(workspaceId)}/credits`

/** True for an owner-protection override (the E3 snapshot), whatever the server flag says. */
export function isOwnerProtected(note: string | null | undefined): boolean {
  return (note ?? '').trim().toLowerCase().startsWith(OWNER_PROTECTION_PREFIX)
}

/** Human text for a value of a feature. */
export function formatValue(feature: Pick<RegistryFeature, 'kind'> | null, value: FeatureValue): string {
  if (value === null) return 'Unlimited'
  if (typeof value === 'boolean') return value ? 'On' : 'Off'
  if (typeof value === 'number') return value.toLocaleString()
  if (feature?.kind === 'enum') return value
  return String(value)
}

/** "bool", "int (unlimited allowed)", "enum: credits | monthly". */
export function kindLabel(f: RegistryFeature): string {
  if (f.kind === 'int') return f.nullable ? 'int · unlimited allowed' : 'int'
  if (f.kind === 'enum') return `enum: ${(f.values ?? []).join(' | ')}`
  return 'bool'
}

/** Editor draft for one value: the raw text plus the "unlimited" toggle. */
export interface ValueDraft {
  raw: string
  unlimited: boolean
}

export function draftFrom(f: RegistryFeature, value: FeatureValue | undefined): ValueDraft {
  const v = value === undefined ? f.default : value
  if (v === null) return { raw: '', unlimited: true }
  return { raw: String(v), unlimited: false }
}

/** Parse an editor draft into a value of the feature's kind, or an error message. */
export function parseDraft(
  f: RegistryFeature,
  draft: ValueDraft,
): { ok: true; value: FeatureValue } | { ok: false; error: string } {
  if (f.kind === 'bool') {
    if (draft.raw === 'true') return { ok: true, value: true }
    if (draft.raw === 'false') return { ok: true, value: false }
    return { ok: false, error: 'Choose On or Off.' }
  }
  if (f.kind === 'enum') {
    if ((f.values ?? []).includes(draft.raw)) return { ok: true, value: draft.raw }
    return { ok: false, error: `Choose one of: ${(f.values ?? []).join(', ')}.` }
  }
  if (draft.unlimited) {
    return f.nullable ? { ok: true, value: null } : { ok: false, error: 'This limit cannot be unlimited.' }
  }
  const text = draft.raw.trim()
  if (!/^\d+$/.test(text)) return { ok: false, error: 'Enter a whole number of 0 or more.' }
  const n = Number(text)
  if (!Number.isSafeInteger(n) || n > MAX_INT) return { ok: false, error: `Enter at most ${MAX_INT}.` }
  return { ok: true, value: n }
}

/** Per-key messages from a 422 invalid_feature_value problem (`errors: [{key, message}]`). */
export function fieldErrors(err: unknown): { key: string; message: string }[] {
  const raw = (err ?? {}) as { errors?: unknown; detail?: unknown }
  const list = Array.isArray(raw.errors) ? raw.errors : []
  return list
    .filter((e): e is { key: string; message: string } => !!e && typeof e === 'object' && 'key' in e)
    .map((e) => ({ key: String(e.key), message: String(e.message) }))
}
